import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { MapPin, Instagram, Youtube } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { INTERESTS } from "../lib/avatars";
import { Avatar, LevelBar, CardTile } from "../components/community/Bits";
import { SetTiles } from "../components/community/Collection";
import { Feed, Leaderboard } from "../components/community/Feed";
import { JoinHero } from "../components/community/JoinParts";
import { StepProfile, StepAvatar, StepComplete } from "../components/community/JoinSteps";

export default function Community() {
  const { user } = useAuth();
  const { tr } = useLang();
  const nav = useNavigate();
  return (
    <div className="bg-[#F7F5FB] min-h-screen">
      {!user && <JoinHero onStart={() => nav("/join")} />}
      {user && (
        <div className="bg-[#120E1F] grain"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-xs uppercase tracking-[0.25em] text-[#F2C230]">Jolgen Cards Community</p>
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-2" data-testid="community-title">{tr("Mehr als nur Karten.", "More than just cards.")}</h1>
        </div></div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid lg:grid-cols-[1fr_340px] gap-8">
        <Feed />
        <aside className="space-y-6 lg:sticky lg:top-36 h-fit"><Leaderboard /></aside>
      </div>
    </div>
  );
}

export function MemberPublic() {
  const { handle } = useParams();
  const { tr, lang } = useLang();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setM(null); api.get(`/community/members/${handle}`).then((r) => setM(r.data)).catch(() => setErr(true)); }, [handle]);
  if (err) return <div className="py-32 text-center text-[#707783]" data-testid="member-not-found">404 · <Link to="/community" className="underline">Community</Link></div>;
  if (!m) return <div className="min-h-[60vh]" />;
  return (
    <div className="bg-[#F7F5FB] min-h-screen">
      <div className="bg-[#120E1F] grain"><div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 flex flex-col sm:flex-row gap-6 items-center sm:items-end">
        <Avatar id={m.avatar} size="h-28 w-28" />
        <div className="flex-1 text-center sm:text-left space-y-2">
          <h1 className="font-display text-4xl text-white" data-testid="public-member-name">{m.display_name}</h1>
          <p className="text-sm text-white/60">{m.about}</p>
          <div className="flex flex-wrap justify-center sm:justify-start gap-3 text-xs text-[#C9B6F2]">
            {m.country && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{m.country}</span>}
            {m.instagram && <span className="flex items-center gap-1"><Instagram className="h-3.5 w-3.5" />{m.instagram}</span>}
            {m.youtube && <span className="flex items-center gap-1"><Youtube className="h-3.5 w-3.5" />{m.youtube}</span>}
          </div>
          <div className="flex flex-wrap justify-center sm:justify-start gap-2">{m.interests.map((i) => { const x = INTERESTS.find((z) => z[0] === i); return <span key={i} className="badge-pill bg-[#8A5CD6]/25 text-[#C9B6F2]">{x ? (lang === "en" ? x[2] : x[1]) : i}</span>; })}</div>
        </div>
        <div className="w-56 space-y-2"><p className="font-display text-2xl text-white">Level {m.level} <span className="text-xs text-[#C9B6F2] font-sans">{m.level_name}</span></p><LevelBar m={m} dark /></div>
      </div></div>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 space-y-8">
        {m.collection.length > 0 && <div className="space-y-3"><h2 className="font-display text-2xl">{tr("Lieblings-Sets", "Favourite sets")}</h2><SetTiles cards={m.collection} /></div>}
        <div className="space-y-3"><h2 className="font-display text-2xl">{tr("Sammlung", "Collection")} <span className="font-mono text-base text-[#6E3FB8]">({m.collection.length})</span></h2>
          <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-4" data-testid="public-collection">{m.collection.map((c) => <CardTile key={c.card_id} c={c} />)}</div></div>
      </div>
    </div>
  );
}

export function EditProfile() {
  const { user, refresh } = useAuth();
  const { tr } = useLang();
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) api.get("/community/me").then((r) => setMe(r.data)); }, [user]);
  if (user === false) return <Navigate to="/login" />;
  if (!me) return <div className="min-h-[60vh]" />;
  const save = async (body) => { setBusy(true); try { const { data } = await api.put("/community/profile", body); setMe(data); await refresh(); toast.success(tr("Gespeichert", "Saved")); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="bg-[#F7F5FB] py-12 px-4" data-testid="edit-profile-page">
      <div className="max-w-xl mx-auto space-y-6">
        <Link to="/member" className="text-sm text-[#6E3FB8]">← {tr("Zurück zum Profil", "Back to profile")}</Link>
        {[StepProfile, StepAvatar, StepComplete].map((S) => <div key={S.name} className="bg-white rounded-[28px] border border-[#120E1F]/5 p-6 sm:p-8"><S initial={S === StepAvatar ? me.avatar : me} busy={busy} onSubmit={save} /></div>)}
      </div>
    </div>
  );
}
